import {computed, Injectable, Injector, Signal, signal} from '@angular/core';
import {Policy, Query as RepositoryQuery} from '../types-repository';
import {Airline, Query as WorkflowQuery} from '../types-workflow';
import {ActivatedRouteSnapshot, NavigationEnd, NavigationSkipped, Router} from '@angular/router';
import {Apollo} from 'apollo-angular';
import {GET_POLICIES} from '../queries-repository';
import {GET_AIRLINES} from '../queries-workflow';
import {HttpHeaders} from '@angular/common/http';
import {catchError, distinctUntilChanged, filter, map, tap} from 'rxjs/operators';
import {combineLatest, EMPTY, of, switchMap} from 'rxjs';
import {BannerService} from '../banner/banner.service';
import {errorText} from '../graphql/error-link';
import {CpOrganization, JanusService} from './janus.service';

@Injectable({
  providedIn: 'root'
})
export class ContextService {
  public organizationId = signal<string | null>(null);
  public organization = signal<CpOrganization | null>(null);
  public airlines = signal<Airline[]>([]);
  /** The organization `airlines` was loaded for; they lag behind `organizationId` during a switch. */
  private airlinesOrg = signal<string | null>(null);
  public airline = signal<Airline | null>(null);
  public policies = signal<Policy[]>(null);
  public licensedProducts = signal<string[]>(null);
  /** Why the organization's context (policies) could not be loaded; null when it loaded. */
  public contextError = signal<string | null>(null);

  /**
   * Whether the principal may browse files at all.
   *
   * A content supplier (see policies_displayinteractive_csp.json) delivers into PO directories
   * spread across airlines it holds no grant for, so it legitimately has *zero* airlines. The
   * airline list therefore cannot be the sole gate for UI access -- file-browsing access is on its
   * own a valid reason to be here. Null-safe because `policies` is null until the first query lands.
   */
  public canBrowseFiles = computed(() =>
    (this.policies() ?? []).some(policy =>
      policy.statements?.some(statement =>
        statement.actions?.includes('contentplatform:repository:Query/Folder')
      )
    )
  );

  /**
   * The airline to work in when there is no choice to make.
   *
   * An organization that holds exactly one airline has nothing to select, so the pages that would
   * ask (workflows, previews) navigate straight into it instead. Null while the airline list is
   * still loading, and for every organization that really does offer a choice.
   */
  public soleAirline = computed<Airline | null>(() => {
    // Only the current organization's airlines count: right after a switch `airlines` still holds the
    // previous organization's, and following its sole airline would navigate into the wrong one.
    if (this.airlinesOrg() !== this.organizationId()) return null;
    const airlines = this.airlines();
    return airlines?.length === 1 ? airlines[0] : null;
  });

  public isAllowed = (action: string, resource: string): Signal<boolean> => {
    return computed(() => {
      const policies = this.policies();

      return policies.some(policy =>
        policy.statements.some(statement => {
          if (!statement.actions.includes(action)) {
            return false;
          }

          if (resource === null) {
            return true;
          }

          const isResourceAllowed = statement.resources.some(statementResource =>
            this.fnMatch(statementResource, resource)
          );

          if (isResourceAllowed && statement.conditions && statement.conditions.length > 0) {
            console.log('Conditions not implemented yet');
          }

          return isResourceAllowed;
        })
      );
    });
  };


  public isAllowedAnywhere(action: string): Signal<boolean> {
    return computed(() => {
      const policies = this.policies();
      return policies.some(
        policy => policy.statements.some(statement => {
            return statement.actions.includes(action);
          }
        )
      )
    });
  }

  constructor(private injector: Injector, private router: Router) {
    console.log('ContextService constructor');
    router.events.pipe(
      filter(event => event instanceof NavigationEnd || event instanceof NavigationSkipped),
      map(() => this.router.routerState.snapshot),
      map((rss) => [
        this.extract(rss.root, 'organization_id'),
        this.extract(rss.root, 'airline')
      ]),
      distinctUntilChanged(([prevOrg, prevAirline], [curOrg, curAirline]) =>
        prevOrg === curOrg && prevAirline === curAirline
      ),
      tap(([organizationId, airlineId]) => console.log('ContextService', organizationId, airlineId) ),
      switchMap(([organizationId, airlineId]) => {
        // A different organization: nothing of the previous one may stay visible (or drive a redirect)
        // while this one loads. Only the airline changing keeps them -- same organization.
        if (organizationId !== this.organizationId()) {
          this.organization.set(null);
          this.policies.set(null);
          this.licensedProducts.set(null);
          this.airlines.set([]);
          this.airline.set(null);
          this.airlinesOrg.set(null);
        }
        this.organizationId.set(organizationId);
        const apollo = this.injector.get(Apollo);
        const janus = this.injector.get(JanusService);

        if (organizationId === null || !organizationId.startsWith('urn:janus:organization:')) {
          this.organization.set(null);
          this.policies.set(null);
          this.licensedProducts.set(null);
          this.airlines.set(null);
          return [];
        }

        this.contextError.set(null);
        const banner = this.injector.get(BannerService);
        // Each request is caught on its own, inside the switchMap: one failure must neither keep the
        // organization from loading nor end the route stream (the next navigation loads again).
        return combineLatest([
          apollo.query<RepositoryQuery>({
            query: GET_POLICIES(organizationId),
            context: {
              headers: new HttpHeaders({
                'X-Castlabs-Organization': organizationId
              })
            }
          }),
          apollo.use('workflow').query<WorkflowQuery>({
            query: GET_AIRLINES(organizationId),
            context: {
              headers: new HttpHeaders({
                'X-Castlabs-Organization': organizationId
              })
            },
          }).pipe(
            map(r => {
              if (!r.data?.airlines) throw r.error ?? new Error('No airlines in the response.');
              return r;
            }),
            // Without airlines the organization still works (files, search); the workflow pages show none.
            catchError(error => {
              banner.open(`Could not load the airlines: ${errorText(error)}`, ['Dismiss']);
              return of({data: {airlines: {airlines: []}}} as any);
            })),
          janus.userinfo$,
        ]).pipe(
          catchError(error => {
            // Without policies nothing can be authorized: say so instead of a blank shell.
            const message = `Could not load the organization's permissions: ${errorText(error)}`;
            this.contextError.set(message);
            banner.open(message, ['Dismiss']);
            return EMPTY;
          }),
          filter(([policiesRes, airlinesRes]) => !!policiesRes.data?.policies && !!airlinesRes.data?.airlines),
          map(([policiesRes, airlinesRes, userinfo]) => {
            // Who the organization is comes from janus; what the user may do in it from the repository
            // API, which resolves the placeholders in the policy documents.
            this.organization.set(JanusService.organizationsOf(userinfo).find(o => o.id === organizationId)
              // Not one of the user's licensed organizations. Still non-null -- as the authorizer's `{}`
              // was before -- so the shell shows "Not Authorized" instead of opening the pages.
              ?? {id: organizationId, name: '', legacy_name: '', parent_name: null});
            this.policies.set(policiesRes.data.policies.policies);
            this.licensedProducts.set(JanusService.licensedServicesOf(userinfo, organizationId));
            const airlines = airlinesRes.data.airlines?.airlines ?? [];
            this.airlines.set([...airlines].sort((a, b) => a.airline_name.localeCompare(b.airline_name)));
            this.airlinesOrg.set(organizationId);

            if (airlineId !== null) {
              const airline = airlinesRes.data.airlines.airlines.find(_airline => _airline.iata_code === airlineId);
              this.airline.set(airline || null);
            } else {
              this.airline.set(null);
            }
          })
        );
      })
    ).subscribe();
  }

  private extract(snapshot: ActivatedRouteSnapshot, param: string): string | null {
    // Walk the route tree to find `organization_id`
    let route: ActivatedRouteSnapshot | null = snapshot;
    while (route) {
      const orgId = route.paramMap.get(param);
      if (orgId) {
        return orgId; // Found it!
      }
      route = route.firstChild; // Move deeper in the route tree
    }
    return null; // Not found
  }


  fnMatch(pattern: string, str: string): boolean {
    // Escape special characters, then replace wildcards with RegEx equivalents
    const regexPattern = pattern
      .replace(/[.+^${}()|[\]\\]/g, '\\$&') // Escape special characters
      .replace(/\*/g, '.*') // Replace * with .*
      .replace(/\?/g, '.'); // Replace ? with .

    const regex = new RegExp(`^${regexPattern}$`);

    return regex.test(str);
  }
}
